// Datos en vivo del KDS (cocina y barra). Portado del KDSBoard anterior:
// sonidos por tipo de evento, rondas nuevas, avisos de cancelación y
// archivado de tarjetas terminadas tras kds_terminadas_seg.
import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { comandasService } from '../../services/api';
import { SOCKET_URL } from '../../utils/socket';
import { useConfigNegocio } from '../../hooks/useConfigNegocio';

export type Estacion = 'todas' | 'cocina' | 'barra';

// Un único AudioContext: crear uno por beep agota el límite del navegador (~6).
let audioCtx: AudioContext | null = null;
let volumen = 1;
function tono(f: number, dur = 0.35, vol = 0.3, inicio = 0) {
  if (volumen <= 0) return;
  try {
    audioCtx = audioCtx || new (window.AudioContext || (window as any).webkitAudioContext)();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const osc = audioCtx.createOscillator(), gain = audioCtx.createGain();
    osc.connect(gain); gain.connect(audioCtx.destination);
    osc.frequency.value = f;
    gain.gain.setValueAtTime(vol * volumen, audioCtx.currentTime + inicio);
    gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + inicio + dur);
    osc.start(audioCtx.currentTime + inicio);
    osc.stop(audioCtx.currentTime + inicio + dur);
  } catch { /* sin audio: el aviso visual basta */ }
}
const beepNueva = () => { tono(660, 0.2, 0.3, 0); tono(880, 0.25, 0.3, 0.22); };
// Ronda extra sobre una mesa ya cerrada: tres tonos, distinto de una comanda nueva.
const beepRonda = () => { tono(880, 0.16, 0.35, 0); tono(660, 0.16, 0.35, 0.18); tono(990, 0.3, 0.35, 0.36); };
const beepCancelada = () => { tono(600, 0.2, 0.35, 0); tono(380, 0.3, 0.35, 0.22); };

// Sin área definida se prepara en cocina; en barra solo lo marcado BARRA (igual que el backend).
export const esDe = (item: any, e: Estacion) =>
  e === 'todas' || (e === 'barra' ? item?.area_impresion === 'BARRA' : item?.area_impresion !== 'BARRA');

const AVISO_MS = 10_000;
const NUEVA_MS = 20_000;
const TICK_MS = 15_000;

export function useKds(estacion: Estacion) {
  const cfg = useConfigNegocio();
  const ambar = parseFloat(cfg.kds_ambar_min) || 10;
  const umbrales: [number, number] = [ambar, Math.max(ambar + 0.5, parseFloat(cfg.kds_rojo_min) || 20)];
  const msTrasLista = Math.max(0, parseFloat(cfg.kds_terminadas_seg ?? '60') || 0) * 1000;
  const msRefresco = Math.max(5, parseInt(cfg.kds_refresco_seg, 10) || 10) * 1000;
  volumen = cfg.kds_sonido === 'false' ? 0 : Math.min(100, Math.max(0, parseInt(cfg.kds_volumen ?? '100', 10) || 0)) / 100;

  const [comandas, setComandas] = useState<any[]>([]);
  const [nuevas, setNuevas] = useState<Set<string>>(new Set());
  const [aviso, setAviso] = useState<{ tipo: 'ronda' | 'modificada' | 'cancelada'; texto: string } | null>(null);
  const [, tick] = useState(0);
  const prevIds = useRef<Set<string>>(new Set());
  const listaDesde = useRef<Record<string, number>>({});
  const entregadas = useRef<Set<string>>(new Set());
  const primera = useRef(true);

  const cargar = useCallback(async () => {
    const r = await comandasService.getActivas().catch(() => null);
    if (!r) return;
    const activas = (r.data || [])
      .filter((c: any) => c.estado !== 'entregada')
      .map((c: any) => ({ ...c, items: (c.items || []).filter((i: any) => i.id && esDe(i, estacion)) }))
      .filter((c: any) => c.items.length > 0);

    const ids = activas.filter((c: any) => !prevIds.current.has(c.id)).map((c: any) => c.id);
    if (ids.length && !primera.current) {
      beepNueva();
      setNuevas(p => new Set([...p, ...ids]));
      setTimeout(() => setNuevas(p => { const s = new Set(p); ids.forEach((id: string) => s.delete(id)); return s; }), NUEVA_MS);
    }
    for (const c of activas) {
      const lista = c.items.every((i: any) => i.estado === 'listo');
      if (lista && !listaDesde.current[c.id]) listaDesde.current[c.id] = Date.now();
      if (!lista) { delete listaDesde.current[c.id]; entregadas.current.delete(c.id); }
    }
    prevIds.current = new Set(activas.map((c: any) => c.id));
    primera.current = false;
    setComandas(activas);
  }, [estacion]);

  useEffect(() => {
    primera.current = true;
    cargar();
    const s = io(SOCKET_URL, { auth: { token: localStorage.getItem('token') } });
    const avisar = (a: typeof aviso) => { setAviso(a); setTimeout(() => setAviso(null), AVISO_MS); };
    s.on('comanda_actualizada', cargar);
    s.on('item_cancelado', (d: any) => {
      beepCancelada();
      avisar({ tipo: 'cancelada', texto: `Cancelado por el mesero: ${(d?.items || []).map((i: any) => `${i.cantidad}× ${i.producto} (Mesa ${i.mesa_numero})`).join(' · ')}` });
      cargar();
    });
    s.on('comanda_modificada', (d: any) => {
      if (d?.ronda_nueva) beepRonda(); else beepNueva();
      avisar(d?.ronda_nueva
        ? { tipo: 'ronda', texto: `Pedido nuevo sobre la Mesa ${d.mesa_numero} · ronda ${d.ronda}, ${d.productos_agregados} producto(s)` }
        : { tipo: 'modificada', texto: `Mesa ${d?.mesa_numero}: se agregaron ${d?.productos_agregados} producto(s)` });
      cargar();
    });
    const i = setInterval(cargar, msRefresco);
    const t = setInterval(() => tick(n => n + 1), TICK_MS); // minutos de espera
    return () => { s.disconnect(); clearInterval(i); clearInterval(t); };
  }, [cargar, msRefresco]);

  // "lista" de cocina y de barra van por separado; en "todas" se cierra completa.
  const cambiarEstado = async (id: string, estado: 'en_preparacion' | 'lista') => {
    await comandasService.updateEstado(id, estado, estacion === 'todas' ? undefined : estacion);
    cargar();
  };

  const terminada = (c: any) => c.items.every((i: any) => i.estado === 'listo');
  // Archivada: la entregaron a mano, o pasó el tiempo configurado desde que quedó lista.
  const archivada = (c: any) => entregadas.current.has(c.id) || (!!listaDesde.current[c.id] && Date.now() - listaDesde.current[c.id] > msTrasLista);
  const entregar = (id: string) => { entregadas.current.add(id); tick(n => n + 1); };

  return { comandas, nuevas, aviso, cerrarAviso: () => setAviso(null), umbrales, cambiarEstado, terminada, archivada, entregar };
}
